import { useState } from "react";
import { C, SERVICES } from "../data/constants";
import { useApi, postTestimonial } from "../hooks/useApi";

// Fallback reviews shown if backend is unavailable
const FALLBACK = [
  { id: 1, name: "Nomvula T.", event: "Proposal Setup", text: "The setup was absolutely breathtaking. He said yes with tears in his eyes. Every detail was perfect — the candles, rose petals, the neon sign. Worth every cent!" },
  { id: 2, name: "Lerato M.", event: "Anniversary", text: "I didn't worry about a single thing on our anniversary night. Moments & Milestones handled everything beautifully. Guests still talk about the décor months later." },
  { id: 3, name: "Sipho K.", event: "Birthday Party", text: "My daughter's 25th birthday was incredible! The balloon arch and backdrop were stunning. Everyone wanted to know who did the setup!" },
  { id: 4, name: "Aisha D.", event: "Valentine's Setup", text: "He organised a Valentine's surprise through them and I was completely speechless. The room was transformed into something magical. 10/10 ✨" },
];

function StarRating() {
  return <div style={{ color: C.blush, fontSize: 16, marginBottom: 12, letterSpacing: 2 }}>★★★★★</div>;
}

function TestimonialCard({ t }) {
  return (
    <div style={{
      background: C.white, borderRadius: 16, padding: "26px 24px",
      boxShadow: "0 2px 16px rgba(44,40,38,0.07)",
      transition: "transform 0.3s, box-shadow 0.3s",
    }}
      onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 10px 28px rgba(44,40,38,0.13)"; }}
      onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 2px 16px rgba(44,40,38,0.07)"; }}
    >
      <StarRating />
      <p style={{ color: C.mid, lineHeight: 1.8, fontSize: 14, fontStyle: "italic", marginBottom: 20 }}>
        "{t.text}"
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 40, height: 40, borderRadius: "50%", background: C.blush,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: C.white, fontWeight: 600, fontSize: 16, flexShrink: 0,
        }}>
          {t.name[0]}
        </div>
        <div>
          <p style={{ fontWeight: 600, color: C.dark, fontSize: 14 }}>{t.name}</p>
          <p style={{ color: C.slate, fontSize: 12 }}>{t.event}</p>
        </div>
      </div>
    </div>
  );
}

export default function Testimonials() {
  const { data, loading } = useApi("/testimonials");
  const apiList = data?.data || [];
  // Use API data if available and non-empty, else show fallback
  const testimonialList = apiList.length > 0 ? apiList : FALLBACK;

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", event: "", text: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr(null);
    try {
      await postTestimonial(form);
      setDone(true);
      setForm({ name: "", event: "", text: "" });
      setTimeout(() => { setDone(false); setShowForm(false); }, 4000);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="testimonials" style={{ padding: "80px 24px", background: C.linen }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <p style={{ color: C.blush, letterSpacing: 3, textTransform: "uppercase", fontSize: 11, marginBottom: 10 }}>Kind Words</p>
          <h2 style={{ fontSize: "clamp(32px, 4vw, 48px)", fontWeight: 300, color: C.dark }}>What Our Clients Say</h2>
          <div style={{ width: 50, height: 1, background: C.blush, margin: "18px auto 0" }} />
        </div>

        {/* Reviews grid */}
        {loading ? (
          <p style={{ textAlign: "center", color: C.slate, fontStyle: "italic", marginBottom: 40 }}>Loading reviews…</p>
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 20,
            marginBottom: 40,
          }}>
            {testimonialList.map((t, i) => <TestimonialCard key={t.id || i} t={t} />)}
          </div>
        )}

        {/* Leave a review button */}
        <div style={{ textAlign: "center", marginBottom: showForm ? 32 : 0 }}>
          {!showForm && (
            <button
              onClick={() => setShowForm(true)}
              style={{ background: "none", border: `1px solid ${C.blush}`, color: C.blush, padding: "11px 28px", borderRadius: 24, fontSize: 14, cursor: "pointer" }}
            >
              Leave a Review 
            </button>
          )}
        </div>

        {/* Review form */}
        {showForm && (
          <div style={{ maxWidth: 520, margin: "0 auto", background: C.white, borderRadius: 18, padding: "30px 28px", boxShadow: "0 4px 20px rgba(44,40,38,0.1)" }}>
            {done ? (
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🌸</div>
                <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: C.dark, marginBottom: 8 }}>Thank you!</h3>
                <p style={{ color: C.mid }}>Your review will appear after approval.</p>
              </div>
            ) : (
              <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: C.dark, marginBottom: 4 }}>Share Your Experience</h3>

                {[["name","Your Name"],["event",""]].map(([field, label]) => (
                  field === "event" ? (
                    <div key={field}>
                      <label style={{ color: C.slate, fontSize: 11, letterSpacing: 1, display: "block", marginBottom: 6, textTransform: "uppercase" }}>Event Type *</label>
                      <select required value={form.event} onChange={e => setForm({ ...form, event: e.target.value })}
                        style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.linen}`, borderRadius: 8, fontSize: 14, color: C.dark, background: C.cream }}>
                        <option value="">Select…</option>
                        {SERVICES.map(s => <option key={s.title}>{s.title}</option>)}
                      </select>
                    </div>
                  ) : (
                    <div key={field}>
                      <label style={{ color: C.slate, fontSize: 11, letterSpacing: 1, display: "block", marginBottom: 6, textTransform: "uppercase" }}>{label} *</label>
                      <input required value={form[field]} onChange={e => setForm({ ...form, [field]: e.target.value })}
                        style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.linen}`, borderRadius: 8, fontSize: 14, color: C.dark, background: C.cream }} />
                    </div>
                  )
                ))}

                <div>
                  <label style={{ color: C.slate, fontSize: 11, letterSpacing: 1, display: "block", marginBottom: 6, textTransform: "uppercase" }}>Your Review *</label>
                  <textarea required rows={4} value={form.text} onChange={e => setForm({ ...form, text: e.target.value })}
                    placeholder="Tell us about your experience…"
                    style={{ width: "100%", padding: "10px 14px", border: `1px solid ${C.linen}`, borderRadius: 8, fontSize: 14, color: C.dark, background: C.cream, resize: "vertical" }} />
                </div>

                {err && <p style={{ color: "#b00020", fontSize: 13 }}>{err}</p>}

                <div style={{ display: "flex", gap: 10 }}>
                  <button type="button" onClick={() => setShowForm(false)}
                    style={{ flex: 1, background: "none", border: `1px solid ${C.linen}`, color: C.slate, padding: "11px", borderRadius: 24, fontSize: 14, cursor: "pointer" }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={submitting}
                    style={{ flex: 1, background: C.blush, color: C.white, border: "none", padding: "11px", borderRadius: 24, fontSize: 14, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? 0.7 : 1 }}>
                    {submitting ? "Sending…" : "Submit Review"}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}
      </div>
    </section>
  );
}